import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'glow_admin_token';
const EMAIL_KEY = 'glow_admin_email';

const authHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Download a protected CSV endpoint by fetching with auth, then triggering a blob download.
const downloadCsv = async (path, filename) => {
  try {
    const res = await fetch(`${API}${path}`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`Export failed (${res.status})`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    alert(e.message || 'Export failed');
  }
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch { return iso; }
};

const SOURCE_LABELS = {
  hormone_assessment: 'Assessment',
  hormone_guide_popup: 'Guide Popup',
  eligibility_prescreen: 'Prescreen',
  intake_abandoned: 'Intake Abandoned',
  book_abandoned_coupon: 'Book Abandoned (Coupon)',
  perimenopause_exit_intent_checklist: 'Perimenopause Exit',
  menopause_exit_intent_checklist: 'Menopause Exit',
  pmos_exit_intent_checklist: 'PMOS Exit',
  pmdd_exit_intent_checklist: 'PMDD Exit',
  postpartum_exit_intent_checklist: 'Postpartum Exit',
  thyroid_exit_intent_checklist: 'Thyroid Exit',
};

const niceSource = (s) => SOURCE_LABELS[s] || s || 'unknown';

// ============ Login ============
const LoginView = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(EMAIL_KEY, data.email);
      onLogin();
    } catch (err) {
      setError(err.response?.data?.detail || 'Sign in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login" data-testid="admin-login">
      <div className="admin-login-card">
        <div className="admin-login-brand">Glow Health</div>
        <h1 className="admin-login-title">Admin Dashboard</h1>
        <p className="admin-login-sub">Sign in to view leads and assessments.</p>
        <form onSubmit={submit}>
          <label className="admin-field">
            <span>Email</span>
            <input
              type="email" required autoComplete="username"
              value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
            />
          </label>
          <label className="admin-field">
            <span>Password</span>
            <input
              type="password" required autoComplete="current-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
            />
          </label>
          {error && <div className="admin-login-error" data-testid="admin-login-error">{error}</div>}
          <button
            type="submit" disabled={loading}
            className="admin-login-btn"
            data-testid="admin-login-submit"
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ============ Stats row ============
const StatsRow = ({ stats }) => {
  if (!stats) return null;
  return (
    <div className="admin-stats-grid" data-testid="admin-stats">
      <div className="admin-stat-card">
        <div className="admin-stat-label">Total Leads</div>
        <div className="admin-stat-value">{stats.total_leads}</div>
        <div className="admin-stat-sub">+{stats.leads_last_7d} this week</div>
      </div>
      <div className="admin-stat-card">
        <div className="admin-stat-label">Assessments</div>
        <div className="admin-stat-value">{stats.total_assessments}</div>
        <div className="admin-stat-sub">+{stats.assessments_last_7d} this week</div>
      </div>
      <div className="admin-stat-card">
        <div className="admin-stat-label">Intake Submissions</div>
        <div className="admin-stat-value">{stats.total_intake_submissions}</div>
        <div className="admin-stat-sub">all time</div>
      </div>
      <div className="admin-stat-card admin-stat-card-wide">
        <div className="admin-stat-label">Top Sources</div>
        <div className="admin-source-list">
          {(stats.leads_by_source || []).slice(0, 4).map((s) => (
            <div key={s.source} className="admin-source-chip">
              <span>{niceSource(s.source)}</span>
              <strong>{s.count}</strong>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============ Leads tab ============
const LeadsTab = () => {
  const [data, setData] = useState({ leads: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit, offset });
      if (source) params.set('source', source);
      if (search) params.set('search', search);
      const { data } = await axios.get(`${API}/admin/leads?${params}`, { headers: authHeaders() });
      setData(data);
      setSelectedIds(new Set()); // reset selection on each page change
    } catch (e) {
      setData({ leads: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [source, search, offset]);

  useEffect(() => { load(); }, [load]);

  const onSearch = (e) => {
    e.preventDefault();
    setOffset(0);
    load();
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allOnPageSelected =
    data.leads.length > 0 &&
    data.leads.every((l) => l.id && selectedIds.has(l.id));

  const toggleAllOnPage = () => {
    if (allOnPageSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(data.leads.filter((l) => l.id).map((l) => l.id)));
    }
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    const n = selectedIds.size;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${n} lead${n > 1 ? 's' : ''}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await axios.post(
        `${API}/admin/leads/delete`,
        { ids: Array.from(selectedIds) },
        { headers: authHeaders() }
      );
      setSelectedIds(new Set());
      await load();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(data.total / limit));

  return (
    <div className="admin-panel" data-testid="admin-leads-panel">
      <div className="admin-panel-head">
        <div className="admin-filters">
          <form onSubmit={onSearch}>
            <input
              type="search" placeholder="Search by email or name..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-leads-search"
            />
          </form>
          <select
            value={source}
            onChange={(e) => { setSource(e.target.value); setOffset(0); }}
            data-testid="admin-leads-source-filter"
          >
            <option value="">All sources</option>
            {Object.entries(SOURCE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        <div className="admin-panel-head-right">
          <button
            type="button"
            className="admin-export-btn"
            onClick={() => {
              const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
              downloadCsv('/admin/leads/export.csv', `glow_leads_${stamp}.csv`);
            }}
            data-testid="admin-leads-export"
          >Export CSV</button>
          <span className="admin-count">{data.total} total</span>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="admin-bulk-bar" data-testid="admin-leads-bulk-bar">
          <div className="admin-bulk-bar-left">
            <strong>{selectedIds.size}</strong> selected
            <button
              type="button"
              className="admin-bulk-clear"
              onClick={() => setSelectedIds(new Set())}
              data-testid="admin-leads-clear-selection"
            >Clear</button>
          </div>
          <button
            type="button"
            className="admin-bulk-delete"
            onClick={deleteSelected}
            disabled={deleting}
            data-testid="admin-leads-delete-selected"
          >{deleting ? 'Deleting...' : `Delete ${selectedIds.size}`}</button>
        </div>
      )}

      <div className="admin-table-wrap">
        <table className="admin-table" data-testid="admin-leads-table">
          <thead>
            <tr>
              <th className="admin-checkbox-col">
                <input
                  type="checkbox"
                  className="admin-checkbox"
                  checked={allOnPageSelected}
                  onChange={toggleAllOnPage}
                  aria-label="Select all on page"
                  data-testid="admin-leads-select-all"
                />
              </th>
              <th>Email</th>
              <th>Name</th>
              <th>Source</th>
              <th>Captured</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" className="admin-loading">Loading...</td></tr>
            ) : data.leads.length === 0 ? (
              <tr><td colSpan="5" className="admin-empty">No leads found</td></tr>
            ) : data.leads.map((l) => (
              <tr key={l.id || l.email + l.timestamp}>
                <td className="admin-checkbox-col">
                  <input
                    type="checkbox"
                    className="admin-checkbox"
                    checked={l.id ? selectedIds.has(l.id) : false}
                    onChange={() => l.id && toggleOne(l.id)}
                    disabled={!l.id}
                    aria-label={`Select ${l.email}`}
                    data-testid={`admin-lead-checkbox-${l.id || 'noid'}`}
                  />
                </td>
                <td><a href={`mailto:${l.email}`}>{l.email}</a></td>
                <td>{l.firstName || '—'}</td>
                <td><span className="admin-chip">{niceSource(l.source)}</span></td>
                <td>{formatDate(l.timestamp)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="admin-pagination">
        <button
          onClick={() => setOffset(Math.max(0, offset - limit))}
          disabled={offset === 0 || loading}
          data-testid="admin-leads-prev"
        >Prev</button>
        <span>Page {page} of {totalPages}</span>
        <button
          onClick={() => setOffset(offset + limit)}
          disabled={offset + limit >= data.total || loading}
          data-testid="admin-leads-next"
        >Next</button>
      </div>
    </div>
  );
};

// ============ Assessments tab ============
const AssessmentsTab = () => {
  const [data, setData] = useState({ assessments: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit, offset });
      if (search) params.set('search', search);
      const { data } = await axios.get(`${API}/admin/assessments?${params}`, { headers: authHeaders() });
      setData(data);
    } catch {
      setData({ assessments: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [search, offset]);

  useEffect(() => { load(); }, [load]);

  const onSearch = (e) => { e.preventDefault(); setOffset(0); load(); };

  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(data.total / limit));

  return (
    <div className="admin-panel" data-testid="admin-assessments-panel">
      <div className="admin-panel-head">
        <form onSubmit={onSearch}>
          <input
            type="search" placeholder="Search by email or name..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-assessments-search"
          />
        </form>
        <div className="admin-panel-head-right">
          <button
            type="button"
            className="admin-export-btn"
            onClick={() => {
              const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
              downloadCsv('/admin/assessments/export.csv', `glow_assessments_${stamp}.csv`);
            }}
            data-testid="admin-assessments-export"
          >Export CSV</button>
          <span className="admin-count">{data.total} total</span>
        </div>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table" data-testid="admin-assessments-table">
          <thead>
            <tr>
              <th></th>
              <th>Name</th>
              <th>Email</th>
              <th>Primary Concern</th>
              <th>Status</th>
              <th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="admin-loading">Loading...</td></tr>
            ) : data.assessments.length === 0 ? (
              <tr><td colSpan="6" className="admin-empty">No assessments found</td></tr>
            ) : data.assessments.map((a) => {
              const open = expanded === a.id;
              return (
                <React.Fragment key={a.id}>
                  <tr className={open ? 'is-open' : ''}>
                    <td>
                      <button
                        className="admin-row-toggle"
                        onClick={() => setExpanded(open ? null : a.id)}
                        data-testid={`admin-assessment-toggle-${a.id}`}
                      >{open ? '−' : '+'}</button>
                    </td>
                    <td>{[a.firstName, a.lastName].filter(Boolean).join(' ') || '—'}</td>
                    <td><a href={`mailto:${a.email}`}>{a.email}</a></td>
                    <td>{a.primaryConcern || '—'}</td>
                    <td><span className={`admin-chip status-${a.status || 'submitted'}`}>{a.status || 'submitted'}</span></td>
                    <td>{formatDate(a.timestamp)}</td>
                  </tr>
                  {open && (
                    <tr className="admin-detail-row">
                      <td colSpan="6">
                        <div className="admin-detail-grid">
                          <div><strong>Phone:</strong> {a.phone || '—'}</div>
                          <div><strong>DOB:</strong> {a.dateOfBirth || '—'}</div>
                          <div><strong>State:</strong> {a.state || '—'}</div>
                          <div className="admin-detail-wide">
                            <strong>Symptoms:</strong>{' '}
                            {(a.symptoms || []).length
                              ? (a.symptoms || []).join(', ')
                              : '—'}
                          </div>
                          {a.additionalInfo && (
                            <div className="admin-detail-wide">
                              <strong>Notes:</strong> {a.additionalInfo}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="admin-pagination">
        <button onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0 || loading} data-testid="admin-assessments-prev">Prev</button>
        <span>Page {page} of {totalPages}</span>
        <button onClick={() => setOffset(offset + limit)} disabled={offset + limit >= data.total || loading} data-testid="admin-assessments-next">Next</button>
      </div>
    </div>
  );
};

// ============ Dashboard Shell ============
const Dashboard = ({ onLogout }) => {
  const [tab, setTab] = useState('leads');
  const [stats, setStats] = useState(null);
  const [statsErr, setStatsErr] = useState(false);
  const email = localStorage.getItem(EMAIL_KEY) || '';

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/stats`, { headers: authHeaders() });
        setStats(data);
      } catch (e) {
        if (e.response?.status === 401) onLogout();
        else setStatsErr(true);
      }
    })();
  }, [onLogout]);

  return (
    <div className="admin-shell" data-testid="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <span className="admin-brand">Glow Health</span>
          <span className="admin-header-tag">Admin</span>
        </div>
        <div className="admin-header-right">
          <span className="admin-header-email">{email}</span>
          <button onClick={onLogout} className="admin-logout" data-testid="admin-logout">Sign out</button>
        </div>
      </header>

      <main className="admin-main">
        {statsErr && <div className="admin-login-error">Could not load stats.</div>}
        <StatsRow stats={stats} />

        <nav className="admin-tabs">
          <button
            className={tab === 'leads' ? 'is-active' : ''}
            onClick={() => setTab('leads')}
            data-testid="admin-tab-leads"
          >Leads</button>
          <button
            className={tab === 'assessments' ? 'is-active' : ''}
            onClick={() => setTab('assessments')}
            data-testid="admin-tab-assessments"
          >Assessments</button>
        </nav>

        {tab === 'leads' ? <LeadsTab /> : <AssessmentsTab />}
      </main>
    </div>
  );
};

// ============ Entry ============
const AdminDashboard = () => {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));

  // Validate token once on mount; logout if invalid/expired
  useEffect(() => {
    if (!authed) return;
    axios.get(`${API}/admin/me`, { headers: authHeaders() })
      .catch((e) => {
        if (e.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(EMAIL_KEY);
          setAuthed(false);
        }
      });
  }, [authed]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
    setAuthed(false);
  };

  return authed
    ? <Dashboard onLogout={logout} />
    : <LoginView onLogin={() => setAuthed(true)} />;
};

export default AdminDashboard;
